/**
 * Variant Wizard Image Optimizer - Enhanced Version
 * Aggressively intercepts and optimizes high-resolution images
 * Place in: assets/variant-wizard-optimizer.js
 */

(function() {
  'use strict';
  
  const CONFIG = {
    // Optimal image sizes based on viewport
    sizesMap: {
      320: 500,    // Mobile small
      375: 550,    // Mobile standard
      414: 600,    // Mobile large
      768: 600,    // Tablet
      1024: 1200,  // Desktop small
      1200: 1200,  // Desktop medium
      1400: 1400,  // Desktop large
      1600: 1600   // Desktop XL
    },
    
    // Maximum image size to ever load
    maxWidth: 1200,
    
    // Retry attempts if images not found
    maxRetries: 5,
    retryDelay: 500,
    
    // Selectors for Variant Wizard images
    selectors: [
      // Variant Wizard swiper images
      '.pwzrswiper-lazy',
      '#pwzrswiper-img-0',
      '[id^="pwzrswiper-img-"]',
      '[class*="pwzrswiper"] img',
      '[id*="pwzrswiper"] img',
      
      // Variant Wizard slides
      '.pwzrswiper-slide img',
      '[id^="pwzrswiper-slide-"] img',
      '[class*="pwzrswiper-slide"] img',
      
      // Rio theme media gallery
      '.rio-media-gallery img',
      '[class*="rio-media"] img',
      
      // Generic product images
      '.product__media img',
      '.product-single__media img',
      '.product_main_content img',
      '[data-product-media] img',
      '.grid_item img'
    ],
    // Enable debug logging
    debug: true
  };
  
  // Utility: Log with prefix
  function log(...args) {
    if (CONFIG.debug) {
      console.log('🎯 [Image Optimizer]', ...args);
    }
  }
  
  // Get optimal width for current viewport
  function getOptimalWidth() {
    const vw = window.innerWidth;
    const dpr = window.devicePixelRatio || 1;
    
    // Find the closest breakpoint
    const breakpoints = Object.keys(CONFIG.sizesMap).map(Number).sort((a, b) => a - b);
    let targetWidth = CONFIG.sizesMap[1600];
    
    for (const bp of breakpoints) {
      if (vw <= bp) {
        targetWidth = CONFIG.sizesMap[bp];
        break;
      }
    }
    
    // For high DPR screens, increase size but cap at maxWidth
    // Using 1.5x instead of full DPR to balance quality and performance
    const adjustedWidth = Math.round(targetWidth);
    const finalWidth = Math.min(adjustedWidth, CONFIG.maxWidth);
    
    log(`Viewport: ${vw}px, DPR: ${dpr}, Target: ${finalWidth}px`);
    return finalWidth;
  }
  
  // Optimize Shopify CDN URL
  function optimizeUrl(url, targetWidth) {
    if (!url || typeof url !== 'string') return url;
    if (!url.includes('cdn.shopify.com') && !url.includes('cdn/shop')) return url;
    
    // Remove existing size parameters
    let optimized = url
      .replace(/[?&]width=\d+/g, '')
      .replace(/[?&]height=\d+/g, '')
      .replace(/_\d+x\d+(?=\.(jpg|jpeg|png|webp|gif))/gi, '')
      .replace(/_\d+x(?=\.(jpg|jpeg|png|webp|gif))/gi, '');
    
    // Ensure we have query string separator
    const separator = optimized.includes('?') ? '&' : '?';
    
    // Add optimized width
 
    return `${optimized}${separator}width=${targetWidth}&format=webp`;
  }
  
  // Optimize a single image element
  function optimizeImage(img, targetWidth) {
    if (!img || img.dataset.optimized === 'true') return false;
    
    let modified = false;
    const originalSrc = img.src;
    
    // Optimize main src
    if (img.src && img.src.includes('cdn')) {
      const newSrc = optimizeUrl(img.src, targetWidth);
      if (newSrc !== img.src) {
        img.src = newSrc;
        modified = true;
        log(`✓ Optimized src: ${getFilename(originalSrc)}`);
      }
    }
    
    // Optimize data-src (lazy loading)
    if (img.dataset.src && img.dataset.src.includes('cdn')) {
      const newDataSrc = optimizeUrl(img.dataset.src, targetWidth);
      if (newDataSrc !== img.dataset.src) {
        img.dataset.src = newDataSrc;
        modified = true;
      }
    }
    
    // Optimize srcset
    if (img.srcset && img.srcset.includes('cdn')) {
      const newSrcset = img.srcset
        .split(',')
        .map(src => {
          const parts = src.trim().split(/\s+/);
          const url = parts[0];
          
          // Calculate width based on descriptor or use target
          let width = targetWidth;
          if (parts[1]) {
            const match = parts[1].match(/(\d+)w/);
            if (match) {
              // Cap at our target width
              width = Math.min(parseInt(match[1]), targetWidth);
            }
          }
          
          const optimizedUrl = optimizeUrl(url, width);
          return `${optimizedUrl} ${width}w`;
        })
        .join(', ');
      
      if (newSrcset !== img.srcset) {
        img.srcset = newSrcset;
        modified = true;
        log(`✓ Optimized srcset`);
      }
    }
    
    // Optimize sizes attribute for better browser selection
    if (!img.sizes || img.sizes === '100vw') {
      img.sizes = '(min-width: 1024px) 50vw, (min-width: 768px) 75vw, 100vw';
      modified = true;
    }
    
    // Optimize zoom/lightbox images (use 2x for quality)
    ['data-zoom', 'data-zoom-src', 'data-large', 'data-full'].forEach(attr => {
      if (img.dataset[attr.replace('data-', '')] && img.dataset[attr.replace('data-', '')].includes('cdn')) {
        const zoomWidth = Math.min(targetWidth * 2, CONFIG.maxWidth);
        const attrName = attr.replace('data-', '');
        const newZoom = optimizeUrl(img.dataset[attrName], zoomWidth);
        if (newZoom !== img.dataset[attrName]) {
          img.dataset[attrName] = newZoom;
          modified = true;
        }
      }
    });
    
    // Mark as optimized to avoid re-processing
    if (modified) {
      img.dataset.optimized = 'true';
      img.dataset.optimizedAt = Date.now();
    }
    
    return modified;
  }
  
  // Get filename from URL for logging
  function getFilename(url) {
    return url.split('/').pop().split('?')[0].substring(0, 30);
  }
  
  // Main optimization function
  function optimizeAllImages() {
    const targetWidth = getOptimalWidth();
    const images = document.querySelectorAll(CONFIG.selectors.join(','));
    
    if (images.length === 0) {
      log('⚠️ No images found');
      return 0;
    }
    
    let optimized = 0;
    images.forEach(img => {
      if (optimizeImage(img, targetWidth)) {
        optimized++;
      }
    });
    
    if (optimized > 0) {
      log(`✨ Optimized ${optimized} of ${images.length} images`);
    }
    
    return optimized;
  }
  
  // Retry mechanism for late-loading images
  function optimizeWithRetry(attempt = 1) {
    const count = optimizeAllImages();
    
    if (count === 0 && attempt < CONFIG.maxRetries) {
      log(`Retry ${attempt}/${CONFIG.maxRetries} in ${CONFIG.retryDelay}ms...`);
      setTimeout(() => optimizeWithRetry(attempt + 1), CONFIG.retryDelay);
    }
  }
  
  // Intercept Image creation to optimize at source
  function interceptImageCreation() {
    const originalImage = window.Image;
    
    window.Image = function() {
      const img = new originalImage();
      const targetWidth = getOptimalWidth();
      
      // Intercept src setter
      const originalSrcDescriptor = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src');
      
      Object.defineProperty(img, 'src', {
        get() {
          return originalSrcDescriptor.get.call(this);
        },
        set(value) {
          const optimized = optimizeUrl(value, targetWidth);
          originalSrcDescriptor.set.call(this, optimized);
          if (optimized !== value) {
            log(`✓ Intercepted new Image(): ${getFilename(value)}`);
          }
        }
      });
      
      return img;
    };
  }
  
  // Watch for DOM changes
  function observeDOM() {
    const targetWidth = getOptimalWidth();
    
    const observer = new MutationObserver((mutations) => {
      let needsOptimization = false;
      
      mutations.forEach((mutation) => {
        // Check added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.tagName === 'IMG' || node.querySelector('img')) {
              needsOptimization = true;
            }
          }
        });
        
        // Check attribute changes on images
        if (mutation.type === 'attributes' && 
            mutation.target.tagName === 'IMG' && 
            (mutation.attributeName === 'src' || mutation.attributeName === 'srcset')) {
          optimizeImage(mutation.target, targetWidth);
        }
      });
      
      if (needsOptimization) {
        setTimeout(optimizeAllImages, 100);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset', 'data-src']
    });
    
    log('👀 DOM observer active');
  }
  
  // Handle variant changes
  function handleVariantChange() {
    log('🔄 Variant changed');
    setTimeout(() => {
      optimizeAllImages();
    }, 100);
  }
  
  // Handle window resize
  let resizeTimeout;
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      log('📐 Window resized');
      // Clear optimization flags to allow re-optimization
      document.querySelectorAll('[data-optimized]').forEach(img => {
        img.removeAttribute('data-optimized');
      });
      optimizeAllImages();
    }, 300);
  }
  
  // Initialize
  function init() {
    log('🚀 Initializing...');
    
    // Intercept image creation
    interceptImageCreation();
    
    // Initial optimization
    optimizeAllImages();
    
    // Retry for late-loading images
    optimizeWithRetry();
    
    // Watch for changes
    observeDOM();
    
    // Listen for variant changes (multiple event types)
    ['variant:changed', 'variantchange', 'product:variant:change'].forEach(event => {
      document.addEventListener(event, handleVariantChange);
    });
    
    // Listen for resize
    window.addEventListener('resize', handleResize);
    
    // Re-optimize when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(optimizeAllImages, 100);
      }
    });
    
    // Expose API for debugging
    window.imageOptimizer = {
      optimize: optimizeAllImages,
      getOptimalWidth,
      config: CONFIG
    };
    
    log('✅ Ready');
  }
  
  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();